import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Baby, Rocket, Briefcase, Lock, Check, X, Sparkles, Loader2, Award,
  ArrowLeft, ArrowRight, Wand2, RefreshCw, GraduationCap,
  Compass, Lightbulb, TrendingUp, ShieldAlert, Target, BookOpen,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { Footer } from "@/components/Footer";
import { PROFILES, MODULES } from "@/data/academieModules";

const ICONS = { Baby, Rocket, Briefcase };
const DONE_KEY = "cocoon_academie_done";
const loadDone = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY)) || {}; } catch { return {}; } };

function Quiz({ quiz, onComplete }) {
  const { t } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const [answers, setAnswers] = useState({});
  const answer = (i, val) => { if (answers[i] !== undefined) return; setAnswers((a) => ({ ...a, [i]: val })); };
  const answeredCount = Object.keys(answers).length;
  const score = quiz.reduce((s, q, i) => s + (answers[i] === q.answer ? 1 : 0), 0);
  const done = answeredCount === quiz.length;
  return (
    <div className="space-y-4" data-testid="academie-quiz">
      {quiz.map((q, i) => {
        const given = answers[i];
        const correct = given === q.answer;
        return (
          <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`quiz-q-${i}`}>
            <p className="text-foreground/90 mb-3 font-medium">{q.q}</p>
            <div className="flex gap-2.5">
              <button disabled={given !== undefined} onClick={() => answer(i, true)} data-testid={`quiz-${i}-true`}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors disabled:cursor-default ${given !== undefined ? (q.answer ? "border-emerald-300/50 bg-emerald-300/10 text-emerald-300" : (given === true ? "border-red-400/50 bg-red-400/10 text-red-300" : "border-white/10 text-foreground/40")) : "border-primary/40 text-primary hover:bg-primary/10"}`}>
                {t("quizTrue")}
              </button>
              <button disabled={given !== undefined} onClick={() => answer(i, false)} data-testid={`quiz-${i}-false`}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors disabled:cursor-default ${given !== undefined ? (!q.answer ? "border-emerald-300/50 bg-emerald-300/10 text-emerald-300" : (given === false ? "border-red-400/50 bg-red-400/10 text-red-300" : "border-white/10 text-foreground/40")) : "border-primary/40 text-primary hover:bg-primary/10"}`}>
                {t("quizFalse")}
              </button>
            </div>
            {given !== undefined && (
              <p className={`mt-3 text-sm flex items-start gap-2 ${correct ? "text-emerald-300/90" : "text-red-300/90"}`}>
                {correct ? <Check size={15} className="mt-0.5 flex-shrink-0" /> : <X size={15} className="mt-0.5 flex-shrink-0" />}
                {q.explain}
              </p>
            )}
          </div>
        );
      })}
      {done && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/[0.06] p-5" data-testid="quiz-result">
          <span className="text-foreground/85">{t("score")} : <span className="text-primary font-semibold">{score}/{quiz.length}</span></span>
          <button onClick={onComplete} className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="quiz-continue-btn">
            {t("continueBtn")} <ArrowRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

function PromptSandbox({ age }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const evaluate = async () => {
    if (prompt.trim().length < 3 || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/academie/prompt-eval", { prompt: prompt.trim(), age, lang: (i18n.language || "fr").slice(0, 2) });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("coachError")); }
    finally { setLoading(false); }
  };
  return (
    <div className="rounded-2xl border border-primary/20 glass p-6" data-testid="prompt-sandbox">
      <div className="flex items-center gap-2 text-primary mb-2"><Wand2 size={16} /> <span className="text-sm font-semibold uppercase tracking-wider">{t("sandboxTitle")}</span></div>
      <p className="text-foreground/55 text-sm mb-4">{t("sandboxIntro")}</p>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} maxLength={1500}
        placeholder={t("sandboxPlaceholder")}
        data-testid="sandbox-input"
        className="w-full bg-black/30 border border-white/12 rounded-xl px-4 py-3 text-sm text-foreground/90 outline-none focus:border-primary/40 resize-none" />
      <div className="mt-3">
        <button onClick={evaluate} disabled={loading || prompt.trim().length < 3} data-testid="sandbox-evaluate-btn"
          className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {t("evaluate")}
        </button>
      </div>
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 space-y-4" data-testid="sandbox-result">
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="hsl(0 0% 100% / 0.08)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="hsl(28 96% 56%)" strokeWidth="3" strokeDasharray={`${result.score}, 100`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-display text-primary text-lg" data-testid="sandbox-score">{result.score}</span>
              </div>
              <p className="text-foreground/80 text-sm">{result.feedback}</p>
            </div>
            {result.tips?.length > 0 && (
              <ul className="space-y-1.5">
                {result.tips.map((t, i) => (
                  <li key={i} className="text-sm text-foreground/70 flex items-start gap-2"><ArrowRight size={14} className="text-primary mt-0.5 flex-shrink-0" /> {t}</li>
                ))}
              </ul>
            )}
            {result.improved && (
              <div className="rounded-xl border border-emerald-300/25 bg-emerald-300/[0.05] p-4">
                <div className="text-[0.7rem] uppercase tracking-wider text-emerald-300/80 mb-1.5">{t("improvedLabel")}</div>
                <p className="text-sm text-foreground/80 italic">{result.improved}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Certificate({ module, profileKey }) {
  const { t } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const [name, setName] = useState(() => { try { return localStorage.getItem("cocoon_academie_name") || ""; } catch { return ""; } });
  const [busy, setBusy] = useState(false);
  const label = t([`labels.${profileKey}`, "labels.default"]);
  const loadImg = (src) => new Promise((res) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });
  const download = async () => {
    const nm = (name || "").trim() || "—";
    try { localStorage.setItem("cocoon_academie_name", nm); } catch { /* noop */ }
    setBusy(true);
    try {
      const W = 1200, H = 850;
      const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
      const ctx = cv.getContext("2d");
      ctx.fillStyle = "#0a0806"; ctx.fillRect(0, 0, W, H);
      const glow = ctx.createRadialGradient(W / 2, 300, 40, W / 2, 300, 720);
      glow.addColorStop(0, "rgba(240,155,45,0.24)"); glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(240,170,90,0.4)"; ctx.lineWidth = 3;
      if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(30, 30, W - 60, H - 60, 34); ctx.stroke(); }
      const brand = await loadImg("/cocoon-logo-glow.png");
      if (brand && brand.height) { const bh = 110, bw = bh * (brand.width / brand.height); ctx.save(); ctx.shadowColor = "rgba(240,155,45,0.45)"; ctx.shadowBlur = 30; ctx.drawImage(brand, (W - bw) / 2, 80, bw, bh); ctx.restore(); }
      ctx.textAlign = "center";
      try { ctx.letterSpacing = "6px"; } catch { /* noop */ }
      ctx.fillStyle = "rgba(240,175,95,0.9)"; ctx.font = "600 26px Georgia, serif";
      ctx.fillText(t("certHeading"), W / 2, 260);
      try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
      ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = "italic 26px Georgia, serif";
      ctx.fillText(t("certTo"), W / 2, 340);
      ctx.fillStyle = "#f4ede2"; ctx.font = "700 60px Georgia, serif";
      ctx.fillText(nm.slice(0, 30), W / 2, 410);
      ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "700 40px Georgia, serif";
      ctx.fillText(`« ${label} »`, W / 2, 490);
      ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "400 24px Georgia, serif";
      ctx.fillText(t("certModule", { title: module.title }), W / 2, 560);
      ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.font = "400 22px Georgia, serif";
      ctx.fillText(new Date().toLocaleDateString(), W / 2, 610);
      ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "700 30px Georgia, serif";
      ctx.fillText("cocoonmind.com", W / 2, H - 90);
      cv.toBlob((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a"); a.href = url; a.download = "certificat-cocoon-ia.png"; a.click(); URL.revokeObjectURL(url);
        toast.success(t("certOk"));
      }, "image/png");
    } catch { toast.error(t("certErr")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/[0.06] p-6 text-center" data-testid="academie-certificate">
      <Award size={34} className="text-primary mx-auto mb-3" />
      <p className="font-display text-2xl text-gradient-orange mb-1 pb-[0.06em]">{t("moduleDone")}</p>
      <p className="text-foreground/60 text-sm mb-5">{t("certGet", { label })}</p>
      <div className="flex flex-wrap items-center justify-center gap-2 max-w-md mx-auto">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} placeholder={t("certName")} data-testid="certificate-name-input"
          className="flex-1 min-w-0 bg-black/30 border border-white/12 rounded-full px-4 py-2.5 text-sm text-foreground/90 outline-none focus:border-primary/40" />
        <button onClick={download} disabled={busy} data-testid="certificate-download-btn"
          className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 flex-shrink-0">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Award size={15} />} {t("certBtn")}
        </button>
      </div>
    </div>
  );
}

function ModuleRunner({ module, profileKey, onBack, onDone }) {
  const { t } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const [step, setStep] = useState("content"); // content | quiz | done
  const age = profileKey;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="academie-module-runner">
      <button onClick={onBack} className="text-sm text-foreground/60 hover:text-primary inline-flex items-center gap-1.5 mb-6" data-testid="module-back-btn"><ArrowLeft size={15} /> {t("moduleBack")}</button>
      <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl leading-tight pb-[0.06em] mb-6" data-testid="module-title">{module.title}</h2>

      {step === "content" && (
        <div className="space-y-6" data-testid="module-content">
          {module.sections.map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <h3 className="font-display text-xl text-primary/90 mb-2">{s.h}</h3>
              <p className="text-foreground/75 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.p.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground/95">$1</strong>') }} />
            </div>
          ))}
          {module.sandbox && <PromptSandbox age={age} />}
          <div className="flex justify-end">
            <button onClick={() => setStep("quiz")} className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" data-testid="module-to-quiz-btn">
              {t("toQuiz")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === "quiz" && (
        <Quiz quiz={module.quiz} onComplete={() => { onDone(module.id); setStep("done"); }} />
      )}

      {step === "done" && <Certificate module={module} profileKey={profileKey} />}
    </motion.div>
  );
}

function EvoluerParcours() {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const te = (k) => t(`evoluer.${k}`);
  const lang = (i18n.language || "fr").slice(0, 2);
  const [metier, setMetier] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);

  const generate = async () => {
    const m = metier.trim();
    if (m.length < 2 || loading) return;
    setLoading(true);
    try {
      const { data: res } = await api.post("/academie/evoluer", { metier: m, lang });
      setData(res);
      setStep(0);
      setTimeout(() => document.getElementById("evoluer-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || te("error"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setData(null); setMetier(""); setStep(0); };

  const steps = data
    ? [
        { key: "transfo", label: te("stepTransfo"), icon: TrendingUp },
        { key: "comp", label: te("stepComp"), icon: Compass },
        { key: "cours", label: te("stepCours"), icon: BookOpen },
        { key: "exo", label: te("stepExo"), icon: Target },
        ...(data.verification?.length ? [{ key: "verif", label: te("stepVerif"), icon: Check }] : []),
        { key: "pistes", label: te("stepPistes"), icon: Lightbulb },
      ]
    : [];
  const cur = steps[step]?.key;
  const goNext = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const goPrev = () => setStep((s) => Math.max(0, s - 1));
  const isLast = step === steps.length - 1;

  return (
    <div className="rounded-3xl border border-primary/25 bg-gradient-to-b from-primary/[0.08] to-transparent p-6 sm:p-8" data-testid="evoluer-parcours">
      <div className="flex items-center gap-2 text-primary mb-2">
        <Briefcase size={16} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em]">{te("badge")}</span>
      </div>
      <h2 className="font-display text-gradient-orange text-2xl sm:text-3xl leading-tight pb-[0.06em]" data-testid="evoluer-title">{te("title")}</h2>
      <p className="mt-3 text-foreground/65 text-sm sm:text-base leading-relaxed max-w-2xl">{te("intro")}</p>

      {!data && (
        <div className="mt-5 flex flex-wrap items-center gap-2.5">
          <input
            value={metier}
            onChange={(e) => setMetier(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
            maxLength={120}
            placeholder={te("placeholder")}
            data-testid="evoluer-input"
            className="flex-1 min-w-[220px] bg-black/30 border border-white/12 rounded-full px-5 py-3 text-sm text-foreground/90 outline-none focus:border-primary/40"
          />
          <button onClick={generate} disabled={loading || metier.trim().length < 2} data-testid="evoluer-generate-btn"
            className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-50 flex-shrink-0">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {loading ? te("loading") : te("generate")}
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {data && (
          <motion.div key="result" id="evoluer-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6" data-testid="evoluer-result">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <p className="font-display text-xl text-foreground/90" data-testid="evoluer-result-title">{data.titre}</p>
              <button onClick={reset} data-testid="evoluer-reset-btn" className="text-xs text-foreground/50 hover:text-primary inline-flex items-center gap-1.5">
                <RefreshCw size={13} /> {te("regenerate")}
              </button>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-1.5 mb-6 overflow-x-auto scroll-thin pb-1" data-testid="evoluer-stepper">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const on = i === step;
                const passed = i < step;
                return (
                  <button key={s.key} onClick={() => setStep(i)} data-testid={`evoluer-step-${s.key}`}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs whitespace-nowrap border transition-colors ${on ? "border-primary/50 bg-primary/15 text-primary" : passed ? "border-emerald-300/30 text-emerald-300/80" : "border-white/10 text-foreground/45 hover:text-foreground/70"}`}>
                    {passed ? <Check size={12} /> : <Icon size={12} />} {s.label}
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 min-h-[180px]" data-testid={`evoluer-panel-${cur}`}>
              {cur === "transfo" && (
                <div className="space-y-5">
                  <p className="text-foreground/80 leading-relaxed">{data.transformation}</p>
                  {data.opportunites?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-primary/90 text-sm font-semibold mb-2"><Lightbulb size={15} /> {te("opportunites")}</div>
                      <ul className="space-y-1.5">
                        {data.opportunites.map((o, i) => (
                          <li key={i} className="text-sm text-foreground/70 flex items-start gap-2"><ArrowRight size={14} className="text-primary mt-0.5 flex-shrink-0" /> {o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.risques?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-amber-300/90 text-sm font-semibold mb-2"><ShieldAlert size={15} /> {te("risques")}</div>
                      <ul className="space-y-1.5">
                        {data.risques.map((r, i) => (
                          <li key={i} className="text-sm text-foreground/70 flex items-start gap-2"><span className="text-amber-300/70 mt-0.5">•</span> {r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {cur === "comp" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.competences.map((c, i) => (
                    <div key={i} className="rounded-xl border border-primary/15 bg-primary/[0.04] p-4">
                      <div className="flex items-center gap-2 text-primary/90 font-semibold mb-1"><Compass size={14} /> {c.nom}</div>
                      <p className="text-sm text-foreground/65 leading-relaxed">{c.pourquoi}</p>
                    </div>
                  ))}
                </div>
              )}

              {cur === "cours" && (
                <div className="space-y-3">
                  {data.mini_cours.map((c, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex items-center gap-2 text-foreground/90 font-display text-lg mb-1.5"><BookOpen size={15} className="text-primary/80" /> {c.titre}</div>
                      <p className="text-sm text-foreground/70 leading-relaxed">{c.contenu}</p>
                    </div>
                  ))}
                </div>
              )}

              {cur === "exo" && (
                <div className="space-y-3">
                  {data.exercices.map((ex, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex items-start gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">{i + 1}</span>
                        <div>
                          <p className="text-foreground/85 leading-relaxed">{ex.consigne}</p>
                          {ex.astuce && <p className="mt-2 text-xs text-primary/75 flex items-start gap-1.5"><Sparkles size={12} className="mt-0.5 flex-shrink-0" /> <span><span className="uppercase tracking-wider">{te("exoAstuce")}</span> — {ex.astuce}</span></p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {cur === "verif" && (
                <div>
                  <p className="text-foreground/70 text-sm mb-4">{te("verifIntro")}</p>
                  <Quiz quiz={data.verification} onComplete={goNext} />
                </div>
              )}

              {cur === "pistes" && (
                <div className="space-y-3">
                  <p className="text-foreground/70 text-sm mb-1">{te("pistesIntro")}</p>
                  {data.pistes.map((p, i) => (
                    <div key={i} className="rounded-xl border border-primary/15 bg-primary/[0.04] p-4">
                      <div className="flex items-center gap-2 text-primary/90 font-semibold mb-1"><Lightbulb size={14} /> {p.titre}</div>
                      <p className="text-sm text-foreground/65 leading-relaxed">{p.description}</p>
                    </div>
                  ))}
                  <div className="rounded-xl border border-emerald-300/25 bg-emerald-300/[0.05] p-4 text-center mt-4">
                    <p className="text-emerald-300/90 font-display text-lg" data-testid="evoluer-done">{te("done")}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between gap-3 mt-5">
              <button onClick={goPrev} disabled={step === 0} data-testid="evoluer-prev-btn"
                className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-primary disabled:opacity-30 disabled:cursor-default">
                <ArrowLeft size={15} /> {te("prev")}
              </button>
              {!isLast && cur !== "verif" && (
                <button onClick={goNext} data-testid="evoluer-next-btn"
                  className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold">
                  {te("next")} <ArrowRight size={15} />
                </button>
              )}
              {isLast && (
                <button onClick={reset} data-testid="evoluer-finish-btn"
                  className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold">
                  {te("finish")} <RefreshCw size={15} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AcademieIA() {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: "atelier.academie" });
  const lang = (i18n.language || "fr").slice(0, 2);
  const { user } = useAuth();
  const [profile, setProfile] = useState("junior");
  const [openModule, setOpenModule] = useState(null);
  const [done, setDone] = useState(loadDone);
  const [profilesTr, setProfilesTr] = useState(PROFILES);
  const [modulesCache, setModulesCache] = useState(lang === "fr" ? MODULES : {});
  const [translating, setTranslating] = useState(false);

  // Changement de langue : réinitialise les caches et les libellés de profil.
  useEffect(() => {
    setOpenModule(null);
    if (lang === "fr") { setProfilesTr(PROFILES); setModulesCache(MODULES); return; }
    setProfilesTr(PROFILES);
    setModulesCache({});
    try {
      const pc = localStorage.getItem(`cocoon_academie_profiles_${lang}_v1`);
      if (pc) setProfilesTr(JSON.parse(pc));
    } catch { /* noop */ }
  }, [lang]);

  // Traduit UNIQUEMENT les modules du profil ouvert, à la demande (+ cache) → 1er chargement bien plus rapide.
  useEffect(() => {
    if (lang === "fr" || modulesCache[profile]) return;
    const key = `cocoon_academie_${lang}_${profile}_v1`;
    try {
      const cached = localStorage.getItem(key);
      if (cached) { setModulesCache((c) => ({ ...c, [profile]: JSON.parse(cached) })); return; }
    } catch { /* noop */ }
    let cancelled = false;
    setTranslating(true);
    api.post("/academie/translate", { lang, profiles: PROFILES, modules: { [profile]: MODULES[profile] } })
      .then(({ data }) => {
        if (cancelled || !data?.payload) return;
        const mods = (data.payload.modules && data.payload.modules[profile]) || MODULES[profile];
        setModulesCache((c) => ({ ...c, [profile]: mods }));
        try { localStorage.setItem(key, JSON.stringify(mods)); } catch { /* noop */ }
        if (data.payload.profiles) {
          setProfilesTr(data.payload.profiles);
          try { localStorage.setItem(`cocoon_academie_profiles_${lang}_v1`, JSON.stringify(data.payload.profiles)); } catch { /* noop */ }
        }
      })
      .catch(() => { if (!cancelled) setModulesCache((c) => ({ ...c, [profile]: MODULES[profile] })); })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [lang, profile, modulesCache]);

  const isSubscriber = !!(user && (user.plan === "cocon" || user.role === "admin"));
  const profileMeta = profilesTr.find((p) => p.key === profile);
  const locked = profileMeta && !profileMeta.free && !isSubscriber;
  const modules = lang === "fr" ? (MODULES[profile] || []) : (modulesCache[profile] || []);

  const markDone = (id) => {
    const next = { ...done, [id]: true };
    setDone(next);
    try { localStorage.setItem(DONE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  };

  const openIt = (m) => {
    if (locked) { toast.error(t("lockedToast")); return; }
    setOpenModule(m);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "880px", height: "620px" }} />

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap" data-testid="academie-brand">
            <img src="/cocoon-logo-glow.png" alt="COCOON MIND" className="h-9 w-auto select-none" style={{ filter: "drop-shadow(0 0 8px hsl(28 96% 56% / 0.30))" }} />
            <span className="font-display text-lg sm:text-2xl tracking-tight"><CocoonMark withMind /></span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors inline-flex items-center gap-1.5"><ArrowLeft size={15} /> {t("home")}</Link>
            {!isSubscriber && <Link to="/tarifs" data-testid="academie-tarifs-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full whitespace-nowrap">{t("subscribe")}</Link>}
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-36 pb-6 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6"><GraduationCap size={14} /> {t("badge")}</span>
        <h1 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-tight pb-[0.06em]" data-testid="academie-title">{t("heroTitle")}</h1>
        <p className="mt-5 text-base sm:text-lg text-foreground/70 max-w-xl mx-auto leading-relaxed">{t("heroSub")}</p>
        {translating && <p className="mt-4 text-sm text-primary/70 inline-flex items-center gap-2" data-testid="academie-translating"><Loader2 size={14} className="animate-spin" /> {t("loading")}</p>}
      </section>

      <div className="max-w-5xl mx-auto px-6 pb-24">
        <section className="mb-12" data-testid="evoluer-section">
          <EvoluerParcours />
        </section>
        {!openModule && (
          <>
            {/* Sélecteur de profil */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10" data-testid="academie-profile-selector">
              {profilesTr.map((p) => {
                const Icon = ICONS[p.icon] || GraduationCap;
                const on = profile === p.key;
                return (
                  <button key={p.key} onClick={() => setProfile(p.key)} data-testid={`profile-${p.key}`}
                    className={`flex flex-col items-center gap-2 rounded-2xl border px-4 py-5 transition-all duration-300 ${on ? "border-primary/50 bg-primary/[0.1] shadow-[0_0_22px_-6px_hsl(28_96%_56%/0.5)]" : "border-white/10 hover:border-primary/25 hover:bg-white/[0.03]"}`}>
                    <Icon size={26} className={on ? "text-primary" : "text-foreground/55"} />
                    <span className={`font-display text-lg ${on ? "text-primary" : "text-foreground/80"}`}>{p.label}</span>
                    <span className="text-xs text-foreground/45">{p.age}</span>
                    {!p.free && <span className="text-[0.6rem] uppercase tracking-wider text-primary/70 inline-flex items-center gap-1"><Lock size={10} /> {t("cocon")}</span>}
                  </button>
                );
              })}
            </div>

            {locked && (
              <div className="rounded-2xl border border-primary/30 bg-primary/[0.06] p-6 text-center mb-8" data-testid="academie-locked-banner">
                <Lock size={26} className="text-primary mx-auto mb-3" />
                <p className="text-foreground/85 mb-1">{t("lockedTitle")}</p>
                <p className="text-foreground/55 text-sm mb-4">{t("lockedDesc")}</p>
                <Link to="/tarifs" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">{t("subscribeCocon")} <ArrowRight size={15} /></Link>
              </div>
            )}

            {/* Cartes modules */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="academie-modules-grid">
              {modules.map((m) => (
                <button key={m.id} onClick={() => openIt(m)} data-testid={`module-card-${m.id}`}
                  className={`relative text-left rounded-2xl border p-6 transition-all duration-300 ${locked ? "border-white/8 opacity-60 cursor-not-allowed" : "border-white/10 hover:border-primary/30 hover:bg-white/[0.03]"}`}>
                  {locked && <Lock size={16} className="absolute top-5 right-5 text-foreground/40" />}
                  {done[m.id] && !locked && <span className="absolute top-5 right-5 w-6 h-6 rounded-full bg-emerald-300/15 text-emerald-300 flex items-center justify-center" data-testid={`module-done-${m.id}`}><Check size={14} /></span>}
                  {m.flagship && <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider text-primary mb-2"><Sparkles size={11} /> {t("flagship")}</span>}
                  <h3 className="font-display text-xl text-foreground/90 mb-1.5 pr-8">{m.title}</h3>
                  <p className="text-foreground/55 text-sm leading-relaxed mb-3">{m.desc}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground/40">
                    <span>{m.minutes} {t("min")}</span>
                    {m.sandbox && <span className="inline-flex items-center gap-1 text-primary/70"><Wand2 size={12} /> {t("sandboxTag")}</span>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {openModule && (
          <ModuleRunner module={openModule} profileKey={profile} onBack={() => setOpenModule(null)} onDone={markDone} />
        )}
      </div>

      <Footer />
    </div>
  );
}
